import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Menu, Sparkles } from 'lucide-react'
import NavbarUser from './NavbarUser'
import NavbarButtons from './NavbarButtons'
import NavbarNavigation from './NavbarNavigation'
import { NavbarMobileSheet } from './NavbarMobileSheet'
import { DarkModeToggle } from '../DarkModeToggle'
import { useAuthz } from '../../auth/useAuthz'
import usSeal from '../../assets/us_seal.svg'
import wLogoClean from '../../assets/w_logo_clean.svg'
import wLogoDark from '../../assets/w_logo_dark.svg'

function Navbar() {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { isWorkflowMember } = useAuthz()

  const currentDate = new Date()
    .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    .toUpperCase()

  return (
    <header className="bg-canvas relative">
      {/* ── Mobile bar (< lg) ──────────────────────────────────────────────── */}
      <div className="lg:hidden flex items-center justify-between px-4 pt-3 pb-2 border-b border-line-strong">
        <button
          onClick={() => setSheetOpen(true)}
          className="p-2 text-fg-secondary hover:text-fg transition-colors"
          aria-label="Open navigation menu"
        >
          <Menu size={20} />
        </button>
        <Link to="/wire" className="font-zodiak text-base font-semibold tracking-wide text-fg">
          WIRe
        </Link>
        <Link to="/wire" aria-label="Wire home">
          <img src={wLogoClean} alt="Wire" className="h-8 w-auto dark:hidden" />
          <img src={wLogoDark} alt="" aria-hidden="true" className="hidden h-8 w-auto dark:block" />
        </Link>
      </div>

      <NavbarMobileSheet open={sheetOpen} onOpenChange={setSheetOpen} />

      {/* ── Desktop navbar (≥ lg) ───────────────────────────────────────────── */}
      <div className="hidden lg:block">
        {/* Badge — absolute, does not affect vertical flow */}
        <div className="absolute left-8 top-0 z-30">
          <Link
            to="/wire"
            className="block bg-canvas border-2 border-line px-4 pt-4 pb-6 shadow-md flex flex-col items-center gap-3 hover:border-line-strong transition-colors"
          >
            <img src={usSeal} alt="US Seal" className="w-20 h-20" />
            <span className="text-xs font-sans font-semibold text-fg-secondary tracking-wider">CIA WIRE</span>
          </Link>
        </div>

        {/* Greeting + search */}
        <div className="pl-40 pr-8 2xl:pr-40 pt-6 pb-2">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between">
            <NavbarUser />
            <NavbarButtons />
          </div>
        </div>

        {/* Divider with centered W logo */}
        <div className="relative">
          <div className="border-t border-line-strong" />
          <div className="absolute left-1/2 -translate-x-1/2 -top-8 z-20">
            <Link to="/wire" aria-label="Wire home">
              <img src={wLogoClean} alt="Wire" className="h-16 w-auto opacity-90 hover:opacity-100 transition-opacity dark:hidden" />
              <img src={wLogoDark} alt="" aria-hidden="true" className="hidden h-16 w-auto opacity-90 hover:opacity-100 transition-opacity dark:block" />
            </Link>
          </div>
        </div>

        {/* Topic / region navigation */}
        <div className="pl-40 pr-8 2xl:pr-40 py-2">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between">
            <NavbarNavigation />
            <div className="flex items-center gap-2">
              <NavLink
                to="/wire/search"
                className={({ isActive }) =>
                  `flex items-center gap-1.5 h-8 rounded-full px-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                    isActive ? 'bg-fill-hover text-fg' : 'text-fg-secondary hover:text-fg'
                  }`
                }
              >
                <Sparkles size={12} aria-hidden="true" />
                HotWIRe AI
              </NavLink>
              {isWorkflowMember && (
                <Link
                  to="/admin"
                  className="flex items-center gap-1.5 rounded-full bg-solid px-3 h-8 text-xs font-bold uppercase tracking-widest text-white hover:bg-solid-hover transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="125.409" height="98.041" viewBox="0 0 125.409 98.041" fill="currentColor" aria-hidden="true" className="h-3 w-auto">
                    <path d="M0,0,48.06,98.041,64.4,69.206,30.278,0Z" />
                    <path d="M40.35,0,88.409,98.041,104.75,69.206,70.627,0Z" />
                    <path d="M80.762,0.005l31.149,63,13.5-23.818L106.434,0.005Z" />
                  </svg>
                  Admin
                </Link>
              )}
              <DarkModeToggle />
            </div>
          </div>
        </div>

        {/* Date */}
        <div className="py-4 px-8">
          <div className="max-w-[1400px] mx-auto text-center">
            <p className="font-serif text-sm tracking-wider text-fg-secondary">{currentDate}</p>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Navbar
